import React from 'react'

export interface LogoProps {
  /**
   * Logo image source
   */
  src: string

  /**
   * Alt text for the logo
   */
  alt?: string

  /**
   * Logo width (number = px, string = any CSS unit)
   */
  width?: number | string

  /**
   * Logo height (number = px, string = any CSS unit)
   */
  height?: number | string

  /**
   * CSS class name
   */
  className?: string

  /**
   * Additional inline styles
   */
  style?: React.CSSProperties

  /**
   * Click handler
   */
  onClick?: () => void

  /**
   * Whether to show hover effect
   */
  interactive?: boolean

  /**
   * Shadow/glow effect (none, soft, medium, strong, neon)
   */
  shadow?: 'none' | 'soft' | 'medium' | 'strong' | 'neon'

  /**
   * Animation style (none, float, pulse, bounce)
   */
  animation?: 'none' | 'float' | 'pulse' | 'bounce'

  /**
   * Called when the image fails to load (e.g. a broken/missing URL). Lets a
   * caller like `TenantLogo` swap in a fallback image instead of showing a
   * broken-image icon.
   */
  onError?: () => void
}

const shadowPresets = {
  none: 'none',
  soft: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
  medium: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2))',
  strong: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))',
  neon: 'drop-shadow(0 0 10px var(--color-primary-glow, rgba(48, 155, 72, 0.6))) drop-shadow(0 0 20px var(--color-primary-glow, rgba(48, 155, 72, 0.3)))',
}

const animationStyles = {
  none: 'none',
  float: 'logoFloat 3s ease-in-out infinite',
  pulse: 'logoPulse 2s ease-in-out infinite',
  bounce: 'logoBounce 1s ease-in-out infinite',
}

/**
 * Logo Component
 *
 * Reusable logo component with optional animations and effects.
 * Used in headers, splash screens, and branding areas.
 *
 * @example
 * ```tsx
 * <Logo
 *   src="/bizfirst-logo.png"
 *   alt="BizFirst"
 *   width={200}
 *   shadow="neon"
 *   animation="float"
 * />
 * ```
 */
export function Logo({
  src,
  alt = 'Logo',
  width = 120,
  height = 'auto',
  className = '',
  style = {},
  onClick,
  interactive = false,
  shadow = 'soft',
  animation = 'none',
  onError,
}: LogoProps): React.ReactElement {
  const widthValue = typeof width === 'number' ? `${width}px` : width
  const heightValue = typeof height === 'number' ? `${height}px` : height

  const computedStyle: React.CSSProperties = {
    width: widthValue,
    height: heightValue,
    objectFit: 'contain',
    filter: shadowPresets[shadow],
    animation: animationStyles[animation],
    cursor: interactive || onClick ? 'pointer' : 'default',
    transition: interactive ? 'transform 0.2s ease, opacity 0.2s ease' : 'none',
    ...style,
  }

  return (
    <>
      <img
        src={src}
        alt={alt}
        className={className}
        style={computedStyle}
        onClick={onClick}
        onError={onError}
        onMouseEnter={(e) => {
          if (interactive) {
            e.currentTarget.style.transform = 'scale(1.05)'
            e.currentTarget.style.opacity = '0.9'
          }
        }}
        onMouseLeave={(e) => {
          if (interactive) {
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.opacity = '1'
          }
        }}
      />

      <style>{`
        @keyframes logoFloat {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.02);
          }
        }

        @keyframes logoPulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        @keyframes logoBounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </>
  )
}

export default Logo
